import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useCategorySelection } from "hooks/categorySelection.hook"
import { useObjectsCategories } from "hooks/api/objectsCategories/objectsCategories.hook"
import { findCategoryNodeForModelNumber } from "helpers/modelNumberCatalogue.helper"
import { useCustomizationFields } from "components/ui/customWebviewer/shared/CustomizationSection/useCustomizationFields.hook"
import {
	COUNTERTOPS_TOP_LEVEL_CATEGORY,
	OTHER_TOP_LEVEL_CATEGORY,
} from "components/ui/customWebviewer/FurnitureSidebar/furniture.constants"
import type { TEref, TSelectedModelNumber } from "types/elevationReferences.type"
import type { TCategoriesWithModels } from "types/modelNumbers.type"

// Countertops and Other are cabinets' siblings in the taxonomy, not kinds of cabinet: each has its
// own + Add entry (REQ-294 / REQ-295) and its own creation panel, so neither may be reachable from
// this one's dropdown.
const EXCLUDED_TOP_LEVEL_CATEGORIES = [COUNTERTOPS_TOP_LEVEL_CATEGORY, OTHER_TOP_LEVEL_CATEGORY]

/**
 * Form state of the "add cabinet" panel (REQ-293) — the create-side counterpart of
 * `useFurnitureForm`. Nothing here is persisted as it changes: unlike the edit sidebar, which
 * commits every field on blur, this form is only read once, when Create object is pressed.
 *
 * `pageErefs` are the elevation references of the page the area was drawn on. Erefs are page-bound
 * and the endpoint rejects a cross-page link, so a re-draw on another page has to drop a selection
 * that no longer belongs.
 */
export const useCreateFurnitureForm = (pageErefs: TEref[]) => {
	const categorySelection = useCategorySelection({ excludedTopLevelCategories: EXCLUDED_TOP_LEVEL_CATEGORIES })
	// Same cached query the selects are built from; here it resolves a catalogue-wide pick's node.
	const { data: objectsCategoriesData } = useObjectsCategories()
	const categoriesData = objectsCategoriesData as unknown as TCategoriesWithModels | undefined

	const [selectedErefId, setSelectedErefId] = useState<number | null>(null)
	// REQ-413/414. Read once, at Create, like everything else here — the customization is applied
	// by a PUT chained onto the create, because its endpoint is per-object.
	const customization = useCustomizationFields()
	const [width, setWidth] = useState<number | null>(null)
	const [height, setHeight] = useState<number | null>(null)
	const [depth, setDepth] = useState<number | null>(null)
	const [selectedModelNumber, setSelectedModelNumber] = useState<TSelectedModelNumber | null>(null)
	/**
	 * The `/categories/` node the picked model number actually belongs to (REQ-398). A displayed
	 * category is split across several nodes — one per column / composite / shelf variant — so the
	 * leaf chosen in the dropdown is not necessarily the leaf the model number hangs off. The POST
	 * has to carry the model number's own node or the endpoint rejects the pair, so this wins over
	 * the dropdown's id when it is known. Cleared with the model number itself.
	 */
	const [modelNumberCategoryId, setModelNumberCategoryId] = useState<number | null>(null)
	// Read synchronously by the category-change effect below, which runs before a state update from
	// the same pick has been committed.
	const modelNumberCategoryIdRef = useRef<number | null>(null)
	/** Why the last pick could not be taken — surfaced in the panel's error slot. */
	const [modelNumberError, setModelNumberError] = useState<string | null>(null)

	const { selectedModelCategoryId } = categorySelection

	// A model number belongs to exactly one category, and the endpoint rejects a pair that doesn't
	// match — so a category change invalidates the choice. Mirrors the edit flow, where the backend
	// disconnects the model number when the category changes.
	const previousCategoryIdRef = useRef(selectedModelCategoryId)
	useEffect(() => {
		if (previousCategoryIdRef.current === selectedModelCategoryId) return
		previousCategoryIdRef.current = selectedModelCategoryId
		// ...unless the category IS the pick's own, i.e. this change is the one a catalogue-wide pick
		// just made (REQ-398) — either adopting the model number's category, or clearing all three
		// because it has none. Wiping here would undo the selection that caused the change.
		if (selectedModelCategoryId === modelNumberCategoryIdRef.current) return
		setSelectedModelNumber(null)
		setModelNumberCategoryId(null)
		modelNumberCategoryIdRef.current = null
		setModelNumberError(null)
	}, [selectedModelCategoryId])

	// Drop an elevation that isn't on the page the area now sits on (a re-draw can move it).
	useEffect(() => {
		if (selectedErefId === null) return
		if (pageErefs.some((eref) => eref.id === selectedErefId)) return
		setSelectedErefId(null)
	}, [pageErefs, selectedErefId])

	const selectedEref = useMemo(
		() => pageErefs.find((eref) => eref.id === selectedErefId) ?? null,
		[pageErefs, selectedErefId],
	)

	// The category is the only thing Create insists on — it is what the endpoint requires beyond the
	// area and the page, and for a cabinet it is also what makes the object a cabinet at all
	// (REQ-293). The dimensions and the model number are optional, exactly as REQ-293 words them
	// ("the app COULD allow the user to specify dimensions ... and select model number at object
	// creation"); both can be filled in afterwards in the object's own sidebar.
	//
	// A dimension that IS typed still has to be a real one — a zero-inch cabinet is either a 400 or a
	// degenerate object. The inputs already refuse 0 (`min`: Ant emits null for a typed zero and
	// clamps to the minimum on blur), so this is the belt to that pair of braces: it guards the
	// request body, which is what the panel is ultimately building.
	const isDimensionUsable = (value: number | null) => value === null || value > 0
	const isValid =
		selectedModelCategoryId !== null &&
		isDimensionUsable(width) &&
		isDimensionUsable(height) &&
		isDimensionUsable(depth)

	/** What the POST sends as `related_category_id` — see `modelNumberCategoryId`. */
	const relatedCategoryId = modelNumberCategoryId ?? selectedModelCategoryId

	/**
	 * Takes a pick from the model-number modal, in either of its scopes (REQ-398).
	 *
	 * A category-scope pick arrives with the node it was listed under. A catalogue-wide one does
	 * not — those rows carry category names, not ids — so its node is resolved from the taxonomy
	 * and the three category selects are moved onto it: with no furniture to migrate, adopting the
	 * model number's category IS how this panel keeps the pair the endpoint demands.
	 *
	 * Two picks are refused rather than half-applied, each with the reason the panel shows:
	 * a model number with no category at all (the same case the modal flags in its footer), and one
	 * belonging to a top level this panel doesn't cover — Countertops and Other are separate + Add
	 * flows, so silently switching a cabinet into one would be wrong.
	 */
	const selectModelNumber = useCallback(
		(model: TSelectedModelNumber | null, categoryNodeId: number | null) => {
			setModelNumberError(null)
			if (model === null) {
				setSelectedModelNumber(null)
				setModelNumberCategoryId(null)
				modelNumberCategoryIdRef.current = null
				return
			}

			const apply = (nodeId: number | null) => {
				modelNumberCategoryIdRef.current = nodeId
				setModelNumberCategoryId(nodeId)
				setSelectedModelNumber(model)
			}

			// Listed under the object's own category: the selects already say the right thing.
			if (categoryNodeId !== null) {
				apply(categoryNodeId)
				return
			}

			const node = findCategoryNodeForModelNumber(categoriesData, model.id)
			if (!node) {
				// There is nothing to infer a category from, and whatever was in the selects belongs to
				// some other model number — keeping it would offer a pair the endpoint rejects. Clear all
				// three levels, take the pick, and say why: Create stays blocked until the model number
				// gets a category (an admin's job) or the user picks a different one.
				categorySelection.resetAll()
				setModelNumberError(
					`${model.model_number} has no category. An admin needs to assign one before it can be used.`,
				)
				apply(null)
				return
			}
			if (EXCLUDED_TOP_LEVEL_CATEGORIES.some((name) => name.toLowerCase() === node.topLevel.toLowerCase())) {
				setModelNumberError(
					`${model.model_number} belongs to ${node.topLevel}, which is not a cabinet. Add it from + Add → ${node.topLevel} instead.`,
				)
				return
			}

			categorySelection.handleTopLevelChange(node.topLevel)
			categorySelection.handleSuperCategoryChange(node.superCategory)
			categorySelection.handleModelCategoryChange(node.id)
			apply(node.id)
		},
		[categoriesData, categorySelection],
	)

	return {
		categorySelection,
		selectedErefId,
		setSelectedErefId,
		selectedEref,
		width,
		setWidth,
		height,
		setHeight,
		depth,
		setDepth,
		selectedModelNumber,
		selectModelNumber,
		customization,
		modelNumberError,
		relatedCategoryId,
		isValid,
	}
}
